import React, { useState, useCallback, useEffect, useRef } from "react";
import { Routes } from "../../../configs";
import { useHistory } from "react-router-dom";
import useModalWithKeyboard from "../../../hooks/useModalWithKeyboard";
import { uploadMyProfileImage } from "../../../modules/ApiHelpers";

import APIService from "../../../modules/apiService";
import AuthService from "../../../modules/authService";
import Helpers from "../../../modules/helpers";
import { EVENT_SHOW_MODAL_INFO } from "../../../constants/Events";
import locales_es from "../../../locales/es";
import { validateEmptyFields } from "../../../utils/validation/forms";
import { EventRegister } from "../../../modules/EventRegister";
import { useTheme } from "../../../configs/Theme";
import { showModalInfo } from "../../../modules/ViewHelpers";
import validateCuitByCountry from "../../../utils/validation/cuit";
import { useDebounce } from "../../SearchPatientPrescriptions/Shared/useSearchPatientPrescription";
import { editableInputTypes } from "@testing-library/user-event/dist/utils";
import { set } from "react-native-reanimated";

export interface IUserData {
  professional_prefix?: { name: string };
  name: string;
  lastname: string;
  idTributario: string;
  country_id: number;
  email: string;
  email_verified_at: string;
  identificationType?: { name: string };
  identification?: string;
  prefix_cell_phone?: string;
  cell_phone?: string;
}

const useBasicInformationPage = () => {
  const history = useHistory();
  const params: any = history.location.state;
  const { theme } = useTheme();

  const api = new APIService();
  const auth = new AuthService();
  const helpers = new Helpers();
  const [errors, setErrors] = useState<any>([]);
  const [load, setLoad] = useState<boolean>(false);
  const [profileMode, setProfileMode] = useState<any>(
    params && params.profileMode ? params.profileMode : false
  );
  const hasSignUp = params && params.hasSignup;
  const [locked, setLocked] = useState<any>(false);
  const [cropping, setCropping] = useState<boolean>(false);
  const [professionalPrefixId, setProfessionalPrefixId] = useState<any>(null);
  const [name, setName] = useState<string>("");
  const [userData, setUserData] = useState<any>(null);
  const [lastname, setLastname] = useState<string>("");
  const [idTributario, setIdTributario] = useState("");
  const [cellphone, setCellphone] = useState<any>("");
  const [countryId, setCountryId] = useState<any>(null);
  const [identificationTypes, setIdentificationTypes] = useState<any>([]);
  const [identification, setIdentification] = useState<any>("");
  const [identificationType, setIdentificationType] = useState<any>(null);
  const [sisa, setSisa] = useState<boolean>(false);
  const imgIdentification: any = useRef();
  const [identificationCropping, setIdentificationCropping] =
    useState<boolean>(false);
  const [identificationImage, setIdentificationImage] = useState<any>(null);
  const [defaultImageFlag, setDefaultImageFlag] = useState<any>(false);
  const [professionalPrefixes, setProfessionalPrefixes] = useState<{
    id?: number | null;
    country_id?: number | null;
    name: string;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
  }>({
    id: null,
    country_id: null,
    name: "",
    created_at: "",
    updated_at: "",
    deleted_at: "",
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>("");
  const [profileImage, setProfileImage] = useState<{ uri: string }>({
    uri: "",
  });

  const nameRef = React.createRef();
  const lastnameRef = React.createRef();
  const idTributarioRef = React.createRef();
  const identificationRef = React.createRef();

  const [errorMessages, setErrorMessages] = useState<{
    name: string;
    lastname: string;
    idTributario: string;
    identification: string;
    required: string;
    identificationImage: string;
  }>({
    name: "",
    lastname: "",
    idTributario: "",
    identification: "",
    required: "El Campo es obligatorio",
    identificationImage: "El Campo es obligatorio",
  });
  const [enableButton, setEnableButton] = useState<boolean>(true);

  const [SISAVerification, setSISAVerification] = useState<boolean>(false);

  const [minDocLength, setMinDocLength] = useState<number>(6);
  const [maxDocLength, setMaxDocLength] = useState<number>(8);
  const debouncedIdentification = useDebounce(identification, 1000);

  useEffect(() => {
    if (debouncedIdentification && identificationType?.id === 1) {
      handleSISAVerification(debouncedIdentification);
      setEnableButton(handleValidateEmptyFields(errors));
    }
  }, [debouncedIdentification]);

  useEffect(() => {
    if (identificationImage && identificationImage.uri) {
      setErrors((prevErrors: any) =>
        prevErrors.filter((error: any) => error !== "identificationImage")
      );
      setErrorMessages((prevMessages: any) => ({
        ...prevMessages,
        identificationImage: "",
      }));
    } else {
      setErrors((prevErrors: any) => [...prevErrors, "identificationImage"]);
      setErrorMessages((prevMessages: any) => ({
        ...prevMessages,
        identificationImage: "El campo es obligatorio",
      }));
    }
  }, [identificationImage]);

  useEffect(() => {
    setEnableButton(handleValidateEmptyFields(errors));
  }, [identificationType, identificationImage]);

  useEffect(() => {
    setEnableButton(handleValidateEmptyFields(errors));
  }, [errors]);

  const onPressPrefixSelectorItem = useCallback(
    (professionalPrefixId: number) => {
      setErrors(errors.filter((item: any) => item !== "professionalPrefixId"));
      setProfessionalPrefixId(professionalPrefixId);
    },
    [errors]
  );

  const [validationDataCuit, setValidationData] = useState({
    validateCuit: false,
    requestCuit: false,
    requestIdTributary: false,
  });

  useEffect(() => {
    const fetchValidationData = async () => {
      try {
        const data = await validateCuitByCountry();
        setValidationData(data);
      } catch (error) {
        console.error("Error fetching validation data:", error);
      }
    };

    fetchValidationData();
  }, []);

  const onSubmit = () => {
    let dataSend = {};
    // si el cuit es requerido en este pais
    dataSend = {
      professionalPrefixId,
      name,
      lastname,
      identification,
    };

    const errors = validateEmptyFields(dataSend);

    if (defaultImageFlag) {
      errors.push("identificationImage");
    }

    if (errors.length) {
      setErrors(errors);
    } else {
      setLoading(true);
      let objData: any   = {};

      objData = {
        cell_phone: cellphone,
        professional_prefix_id: professionalPrefixId,
        name,
        lastname,
        identification_type_id: identificationType.id,
        identification,
        sync_sisa: profileMode ? false : true,
      };

      if (validationDataCuit.requestIdTributary) {
        objData.idTributario = idTributario
      }

      auth
        .updateUser(objData)
        .then((res) => {
          setLoading(false);
          profileMode
            ? history.goBack()
            : history.push(`/${Routes.WorkProfile}`, {
                hasSignup: !!hasSignUp,
                firstProfile: true,
              });
          EventRegister.emit(EVENT_SHOW_MODAL_INFO, {
            title: locales_es.successModal.title,
            message: res.message,
          });
        })
        .catch((err) => {
          setLoading(false);
          EventRegister.emit(EVENT_SHOW_MODAL_INFO, {
            title: locales_es.infoModal.title,
            message: helpers.getErrorMsg(err),
          });
        });
    }
  };

  const IdentificationImageError = (message: string) => {
    showModalInfo(locales_es.errorModal.title, message);
  };

  const handleValidatorInputsTypes = (
    value: any,
    type: string = "",
    setState: any,
    id: string,
    minLength: number = 0
  ) => {
    var allErrors = errors.filter((item: any) => item !== id);
    setErrors(errors.filter((item: any) => item !== id));
    let valueError = false;

    if (type === "cuit") {
      let cleanValue = value.replace(/[^0-9]/g, "");

      if (cleanValue.length > 2) {
        cleanValue = cleanValue.slice(0, 2) + "-" + cleanValue.slice(2);
      }
      if (cleanValue.length > 11) {
        cleanValue = cleanValue.slice(0, 11) + "-" + cleanValue.slice(11);
      }

      cleanValue = cleanValue.slice(0, 13);
      setIdTributario(cleanValue);

      // Solo validar si el CUIT no está vacío
      if (cleanValue.length > 0) {
        if (cleanValue.length !== 13) {
          valueError = true;
          setErrors([...allErrors, id]);
          setErrorMessages({
            ...errorMessages,
            [id]: "El campo debe contener al menos 11 números.",
          });
        } else {
          const cuitRegex = /^\d{2}-\d{8}-\d{1}$/;
          if (!cuitRegex.test(cleanValue)) {
            valueError = true;
            setErrors([...allErrors, id]);
            setErrorMessages({
              ...errorMessages,
              [id]: "Formato de CUIT inválido. El formato correcto es 00-00000000-0",
            });
          } else {
            allErrors = allErrors.filter((e: any) => e !== id);
            setErrors(allErrors);
            setErrorMessages({ ...errorMessages, [id]: "" });
          }
        }
      } else {
        // Si el CUIT está vacío, no agregar errores
        allErrors = allErrors.filter((e: any) => e !== id);
        setErrors(allErrors);
        setErrorMessages({ ...errorMessages, [id]: "" });
      }

      setState(cleanValue);
      setEnableButton(allErrors.length > 0 || valueError);
      return;
    }

    if (
      value.length > 0 &&
      value.length >= minLength &&
      value.trim().length > 0
    ) {
      if (type === "letter") {
        const regex =
          /^[a-zA-ZÀ-ÿ\u00f1\u00d1\u014c\u014d\u016a\u016b\u016c\u016d\u016e\u016f\u0170\u0171\u0172\u0173\u0174\u0175\u0176\u0177\u0178\u0179\u017a\u017b\u017c\u017d\u017e\u012a\u012b\u012c\u012d\u012e\u012f\u0130\u0131 ']*$/;
        if (!regex.test(value)) {
          setState(value);
          setErrors([...errors, id]);
          allErrors.push(id);
          setErrorMessages({
            ...errorMessages,
            [id]: "El Campo solo puede contener letras",
          });
          setEnableButton(handleValidateEmptyFields(allErrors));
          return;
        }
        setState(value);
        setEnableButton(handleValidateEmptyFields(allErrors));
        return;
      }
      if (type === "number") {
        const regex = /^[0-9]*$/;
        if (!regex.test(value)) {
          setState(value);
          setErrors([...errors, id]);
          allErrors.push(id);
          setErrorMessages({
            ...errorMessages,
            [id]: "El Campo solo puede contener números",
          });
          setEnableButton(handleValidateEmptyFields(allErrors));
          return;
        }
        setState(value);
        setEnableButton(handleValidateEmptyFields(allErrors));
        return;
      }

      if (type === "cuit") {
        const cuitRegex = /^\d{2}-\d{8}-\d{1}$/;
        if (!cuitRegex.test(value)) {
          setState(value);
          setErrors([...errors, id]);
          allErrors.push(id);
          setErrorMessages({
            ...errorMessages,
            [id]: "Formato de CUIT inválido. El formato correcto es 00-00000000-0",
          });
          setEnableButton(handleValidateEmptyFields(allErrors));
          return;
        }
        setState(value);
        setEnableButton(handleValidateEmptyFields(allErrors));
        return;
      }

      setState(value);
      setEnableButton(handleValidateEmptyFields(allErrors));
      return;
    } else if (
      value.length > 0 &&
      value.length < minLength &&
      value.trim().length > 0
    ) {
      if (type === "letter") {
        const regex = /^[a-zA-ZÀ-ÿ\u00f1\u00d1 ]*$/;
        if (!regex.test(value)) {
          setState(value);
          setErrors([...errors, id]);
          allErrors.push(id);
          setErrorMessages({
            ...errorMessages,
            [id]:
              "El Campo solo puede contener letras y debe contener al menos " +
              minLength +
              " caracteres",
          });
          setEnableButton(handleValidateEmptyFields(allErrors));
          return;
        }
      }
      if (type === "number") {
        const regex = /^[0-9]*$/;
        if (!regex.test(value)) {
          setState(value);
          setErrors([...errors, id]);
          allErrors.push(id);
          setErrorMessages({
            ...errorMessages,
            [id]:
              "El Campo solo puede contener números y debe contener al menos " +
              minLength +
              " caracteres",
          });
          setEnableButton(handleValidateEmptyFields(allErrors));
          return;
        }
      }
      setState(value);
      setErrors([...errors, id]);
      allErrors.push(id);
      setErrorMessages({
        ...errorMessages,
        [id]: "El Campo debe contener al menos " + minLength + " caracteres",
      });
      setEnableButton(handleValidateEmptyFields(allErrors));
      return;
    }

    setState("");
    setErrors([...errors, id]);
    allErrors.push(id);
    setErrorMessages({ ...errorMessages, [id]: "El Campo es obligatorio" });
    setEnableButton(handleValidateEmptyFields(allErrors));
  };

  const handleValidateEmptyFields = (errors: any) => {
    let getErrors;

    getErrors = validateEmptyFields({
      name,
      lastname,

      identification,
      identificationImage,
    });
      
    if (getErrors.length > 0 || errors.length > 0) {
      return true;
    }
    return false;
  };

  const {
    visible: visibleIdentificationTypesMenuOption,
    open: openIdentificationTypesMenuOption,
    close: closeIdentificationTypesMenuOption,
  } = useModalWithKeyboard(false);

  const onBlur = (inputStr: string) => {
    setErrors(errors.filter((item: any) => item !== inputStr));
  };

  useEffect(() => {
    api
      .getUserByMe()
      .then((res) => {
        if (res && res.data && res.data.user) {
          setUserData(res.data.user);
          const _countryId = res.data.user.country_id;
          const _userData = res.data.user;
          setProfileImage({ uri: _userData.full_profile_image });
          setProfessionalPrefixId(_userData.professional_prefix_id);
          setName(_userData.name);
          setLastname(_userData.lastname);
          setIdTributario(_userData.idTributario);
          setCellphone(_userData.cell_phone);
          setCountryId(_countryId);
          setIdentification(_userData.identification);

          const imagenUrl = _userData.full_identification_image;
          const nombreImagen = imagenUrl ? imagenUrl.split("/").pop() : ""; 
          if (nombreImagen == 'user.png') {
            setIdentificationImage(null);
            setDefaultImageFlag(true);
          } else {
            setIdentificationImage({ uri: imagenUrl });
          } 

          let activate;
            activate =
              _userData.name &&
              _userData.lastname &&
              _userData.idTributario &&
              _userData.identification &&
              _userData.cell_phone;

          if (activate) {
            setEnableButton(false);
          }
          if (_countryId === 1 && !activate) {
            showModalSISAInfo();
          }
          api
            .getIdentificationTypesByCountryId(_countryId)
            .then((res2: any) => {
              if (res2 && res2.data && res2.data.length) {
                setIdentificationTypes(res2.data);

                const userIdType = _userData.identification_type_id;
                if (userIdType) {
                  const idType = res2.data.filter(
                    (type: any) => Number(type.id) === Number(userIdType)
                  )[0];
                  setIdentificationType(idType);
                  setMinDocLength(idType.min);
                  setMaxDocLength(idType.max);
                } else {
                  setIdentificationType(res2.data[0]);
                  setMinDocLength(res2.data[0].min);
                  setMaxDocLength(res2.data[0].max);
                }
              }
            })
            .catch((err2: any) => {
              EventRegister.emit(EVENT_SHOW_MODAL_INFO, {
                title: locales_es.infoModal.title,
                message: helpers.getErrorMsg(err2),
              });
            });
        }
      })
      .catch((err) => {
        console.log(err);
      });
    api
      .getUserProfessionalPrefixes()
      .then((res) => {
        if (res && res.data) {
          setProfessionalPrefixes(res.data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [load]);

  const showNonEditableInfo = () => {
    EventRegister.emit(EVENT_SHOW_MODAL_INFO, {
      title: locales_es.basicProfileInfo.title,
      message: locales_es.basicProfileInfo.content,
    });
  };

  const uploadProfileImage = (image: any) => {
    setCropping(false);
    setLoading(true);
    setLoadingText(locales_es.uploadingImage);
    uploadMyProfileImage(image)
      .then(() => {
        setLoading(false);
        setLoadingText("");
        setProfileImage({
          uri: image,
        });
      })
      .catch(() => {
        setLoading(false);
        setLoadingText("");
      });
  };
  const uploadIdentificationImage = (image: any) => {
    setLoading(true);
    setLoadingText(locales_es.uploadingImage);
    api
      .updateIdentificationImage(image)
      .then((res) => {
        setLoading(false);
        setLoadingText("");
        EventRegister.emit(EVENT_SHOW_MODAL_INFO, {
          title: locales_es.successModal.title,
          message: res.message,
        });
        setDefaultImageFlag(false);
        setIdentificationImage({ uri: image });
      })
      .catch((err) => {
        setLoading(false);
        setLoadingText("");
        EventRegister.emit(EVENT_SHOW_MODAL_INFO, {
          title: "Error",
          message: helpers.getErrorMsg(err),
        });
      });
  };

  const showModalSISAInfo = () => {
    showModalInfo(
      locales_es.SISAInformation.title,
      locales_es.SISAInformation.subtitle
    );
  };

  const handleSISAVerification = (value: string) => {
    setSISAVerification(false);
    auth
      .verificationSISA(value)
      .then((res: any) => {
        if (res.status == true) {
          setSISAVerification(true);
          return;
        }
        setSISAVerification(false);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  return {
    profileMode,
    profileImage,
    identificationCropping,
    uploadProfileImage,
    uploadIdentificationImage,
    setCropping,
    imgIdentification,
    professionalPrefixes,
    professionalPrefixId,
    onPressPrefixSelectorItem,
    errors,
    identificationType,
    showNonEditableInfo,
    openIdentificationTypesMenuOption,
    onBlur,
    identificationRef,
    identification,
    nameRef,
    setIdentification,
    setName,
    name,
    lastnameRef,
    lastname,
    idTributarioRef,
    idTributario,
    setIdTributario,
    setLastname,
    onSubmit,
    visibleIdentificationTypesMenuOption,
    closeIdentificationTypesMenuOption,
    setIdentificationType,
    locked,
    identificationTypes,
    loading,
    loadingText,
    cropping,
    hasSignUp: !!hasSignUp,
    handleSISAVerification,
    handleValidatorInputsTypes,
    enableButton,
    SISAVerification,
    minDocLength,
    maxDocLength,
    countryId,
    showModalSISAInfo,
    setMinDocLength,
    setMaxDocLength,
    setSISAVerification,
    errorMessages,
    validationDataCuit,
    identificationImage,
    sisa,
    setIdentificationCropping,
    setIdentificationImage,
    setSisa,
    IdentificationImageError,
    userData,
    defaultImageFlag
  };
};

export default useBasicInformationPage;
